import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Send,
  MessageSquare,
  Printer,
  Trash2,
  DollarSign,
  Loader2,
  History,
  BellRing,
  Receipt as ReceiptIcon,
  Paperclip,
  ExternalLink,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Bills, fileToBase64 } from "@/lib/api";
import { composeBillEmail, composeBillEmailMeta, composePaymentReceiptEmail, parseEmailList } from "@/lib/email-compose";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import StatusChip from "@/components/StatusChip";
import TagInput from "@/components/TagInput";
import SeriesManager from "@/components/SeriesManager";
import {
  fmtDate,
  fmtDateTime,
  fmtMoney,
  daysUntilDue,
} from "@/lib/bill-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const JobTagsEditor = ({ bill, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState(bill.tags || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await Bills.update(bill.id, { tags });
      onSaved(updated);
      setEditing(false);
      toast.success("Job tags updated.");
    } catch {
      toast.error("Could not save tags");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="job-tags-editor">
      <div className="flex items-center justify-between">
        <span className="swiss-label">Job Tags</span>
        {!editing && (
          <button
            type="button"
            data-testid="tags-edit-btn"
            onClick={() => setEditing(true)}
            className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
          >
            Edit
          </button>
        )}
      </div>
      {!editing ? (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {(bill.tags || []).length === 0 ? (
            <span className="text-neutral-400">—</span>
          ) : (
            bill.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center border border-black px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
              >
                #{t}
              </span>
            ))
          )}
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <TagInput
            testid="bill-tags"
            value={tags}
            onChange={setTags}
            placeholder="Add job tag (e.g. smith-12-hill)"
          />
          <div className="flex gap-2">
            <button
              data-testid="tags-save-btn"
              onClick={save}
              disabled={saving}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => {
                setTags(bill.tags || []);
                setEditing(false);
              }}
              className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CcEditor = ({ bill, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(bill.cc_emails || "");
  const [saving, setSaving] = useState(false);
  const list = parseEmailList(bill.cc_emails);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await Bills.update(bill.id, { cc_emails: value });
      onSaved(updated);
      setEditing(false);
      toast.success(
        list.length
          ? "Cc recipients updated."
          : "Cc recipients saved.",
      );
    } catch {
      toast.error("Could not save recipients");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cc-editor">
      <div className="flex items-center justify-between">
        <span className="swiss-label">Email Cc</span>
        {!editing && (
          <button
            type="button"
            data-testid="cc-edit-btn"
            onClick={() => {
              setValue(bill.cc_emails || "");
              setEditing(true);
            }}
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            {list.length ? "Edit" : "Add"}
          </button>
        )}
      </div>
      {!editing && (
        <div className="mt-1">
          {list.length === 0 ? (
            <span className="text-neutral-500">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {list.map((e) => (
                <span
                  key={e}
                  className="chip bg-neutral-50 text-neutral-700 border-neutral-300"
                >
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && (
        <div className="mt-2 space-y-2">
          <Input
            data-testid="cc-input"
            className="swiss-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="bookkeeper@biz.com, accountant@biz.com"
          />
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Separate with commas. Applied to all emails sent for this bill.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="cc-save-btn"
              onClick={save}
              disabled={saving}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              data-testid="cc-cancel-btn"
              onClick={() => setEditing(false)}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const BillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payReceiptNumber, setPayReceiptNumber] = useState("");
  const [payReceiptBase64, setPayReceiptBase64] = useState(null);
  const [payReceiptMime, setPayReceiptMime] = useState(null);
  const [payReceiptName, setPayReceiptName] = useState("");
  const payReceiptInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const b = await Bills.get(id);
      setBill(b);
    } catch {
      toast.error("Bill not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !bill)
    return (
      <div data-testid="bill-detail-loading" className="text-sm text-neutral-500">
        Loading…
      </div>
    );

  const dleft = daysUntilDue(bill.due_date);

  const doAction = async (fn, okMsg) => {
    setBusy(true);
    try {
      const b = await fn();
      setBill(b);
      toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const onAddPayment = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const updated = await Bills.addPayment(bill.id, {
        amount: amt,
        method: payMethod || undefined,
        note: payNote || undefined,
        bank_receipt_number: payReceiptNumber || undefined,
        receipt_image_base64: payReceiptBase64 || undefined,
        receipt_image_mime: payReceiptMime || undefined,
      });
      setBill(updated);
      setPayOpen(false);
      setPayAmount("");
      setPayMethod("");
      setPayNote("");
      setPayReceiptNumber("");
      setPayReceiptBase64(null);
      setPayReceiptMime(null);
      setPayReceiptName("");
      toast.success("Payment recorded");
    } catch {
      toast.error("Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  const onPickReceipt = async (file) => {
    if (!file) return;
    try {
      const { base64, mime } = await fileToBase64(file);
      setPayReceiptBase64(base64);
      setPayReceiptMime(mime);
      setPayReceiptName(file.name);
    } catch {
      toast.error("Could not read that file.");
    }
  };

  const onEmailPaymentReceipt = (payment) => {
    if (!bill.email) {
      toast.error(
        "No vendor email saved on this bill. Add one first (click Edit) or open your email client manually.",
      );
    }
    const { attachmentName, recipient } = composePaymentReceiptEmail(bill, payment);
    toast.success(
      attachmentName
        ? `Opening email to ${recipient || "vendor"}. Receipt "${attachmentName}" downloaded — attach it before sending.`
        : `Opening email to ${recipient || "vendor"}.`,
    );
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this bill? This cannot be undone.")) return;
    try {
      await Bills.remove(bill.id);
      toast.success("Bill deleted");
      navigate("/");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="bill-detail" className="fade-rise">
      <button
        data-testid="back-btn"
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Bills
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header card */}
          <div className="swiss-card">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <StatusChip status={bill.status} />
              {bill.series_type === "recurring" && bill.series_total && (
                <span
                  data-testid="series-chip"
                  className="chip bg-purple-50 text-purple-800 border-purple-200"
                >
                  Recurring {bill.series_index}/{bill.series_total}
                  {bill.series_frequency ? ` · ${bill.series_frequency}` : ""}
                </span>
              )}
              {bill.series_type === "plan" && bill.series_total && (
                <span
                  data-testid="series-chip"
                  className="chip bg-sky-50 text-sky-800 border-sky-200"
                >
                  Plan {bill.series_index}/{bill.series_total}
                  {bill.series_frequency ? ` · ${bill.series_frequency}` : ""}
                </span>
              )}
              {bill.emailed && (
                <span className="chip bg-blue-50 text-blue-800 border-blue-200">
                  <Mail className="h-3 w-3" /> Emailed
                </span>
              )}
              {bill.texted && (
                <span className="chip bg-indigo-50 text-indigo-800 border-indigo-200">
                  <MessageSquare className="h-3 w-3" /> Texted
                </span>
              )}
            </div>
            <h1
              data-testid="bill-company"
              className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight"
            >
              {bill.company}
            </h1>
            <div
              data-testid="bill-invoice"
              className="hash text-sm text-neutral-500 mt-1 tracking-wider"
            >
              # {bill.invoice_number}
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-neutral-200 pt-6">
              <div className="min-w-0">
                <div className="swiss-label">Total</div>
                <div className="font-display font-extrabold text-lg sm:text-xl mt-1 mono tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
                  {fmtMoney(bill.amount_due)}
                </div>
              </div>
              <div className="min-w-0">
                <div className="swiss-label">Paid</div>
                <div className="font-display font-extrabold text-lg sm:text-xl mt-1 text-green-700 mono tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
                  {fmtMoney(bill.total_paid)}
                </div>
              </div>
              <div className="min-w-0">
                <div className="swiss-label">Remaining</div>
                <div
                  data-testid="bill-remaining"
                  className={`font-display font-extrabold text-lg sm:text-xl mt-1 mono tabular-nums whitespace-nowrap overflow-hidden text-ellipsis ${bill.remaining_balance > 0 ? "text-black" : "text-green-700"}`}
                >
                  {fmtMoney(bill.remaining_balance)}
                </div>
              </div>
              <div className="min-w-0">
                <div className="swiss-label">Due</div>
                <div className="font-semibold text-base mt-1 whitespace-nowrap">
                  {fmtDate(bill.due_date)}
                </div>
                {dleft !== null && (
                  <div
                    className={`text-[10px] uppercase tracking-[0.2em] mt-0.5 ${dleft < 0 ? "text-red-600" : dleft <= 3 ? "text-amber-700" : "text-neutral-500"}`}
                  >
                    {dleft < 0
                      ? `${Math.abs(dleft)}d overdue`
                      : dleft === 0
                        ? "Due today"
                        : `${dleft}d left`}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="record-payment-btn"
                onClick={() => setPayOpen(true)}
                disabled={bill.status === "paid"}
                className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                <DollarSign className="h-4 w-4" /> Record Payment
              </button>
              <button
                data-testid="email-bill-btn"
                onClick={async () => {
                  const res = composeBillEmail(bill);
                  toast.success(
                    res.attachmentName
                      ? `Opening email to ${res.to}. Image "${res.attachmentName}" downloaded — attach before sending, then tap Mark Emailed.`
                      : `Opening email to ${res.to}. Tap Mark Emailed after you've sent it.`,
                  );
                }}
                disabled={busy}
                className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
              >
                <Send className="h-4 w-4" /> Email Bill
              </button>
              <button
                data-testid="copy-email-btn"
                onClick={async () => {
                  const { subject, body, to, cc } = composeBillEmailMeta(bill);
                  const text = `To: ${to}\n${cc.length ? `Cc: ${cc.join(", ")}\n` : ""}Subject: ${subject}\n\n${body}`;
                  try {
                    await navigator.clipboard.writeText(text);
                    toast.success(
                      "Email content copied — paste into Outlook Web / Gmail / any mail app.",
                    );
                  } catch {
                    toast.error(
                      "Couldn't copy. Long-press the Email Bill button to use your mail app.",
                    );
                  }
                }}
                disabled={busy}
                className="border-2 border-neutral-300 bg-white text-neutral-700 font-bold uppercase tracking-widest text-xs px-4 py-3 hover:border-black hover:text-black transition-colors inline-flex items-center gap-2"
                title="Copy the email text if your mail app doesn't open"
              >
                <Copy className="h-4 w-4" /> Copy Email
              </button>
              <button
                data-testid="mark-emailed-btn"
                onClick={() =>
                  doAction(() => Bills.markEmailed(bill.id), "Marked as emailed")
                }
                disabled={busy}
                className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <Mail className="h-4 w-4" /> Mark Emailed
              </button>
              <button
                data-testid="mark-texted-btn"
                onClick={() =>
                  doAction(() => Bills.markTexted(bill.id), "Marked as texted")
                }
                disabled={busy}
                className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" /> Mark Texted
              </button>
              <Link
                to={`/bills/${bill.id}/receipt`}
                data-testid="print-receipt-btn"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <Printer className="h-4 w-4" /> Print Receipt
              </Link>
              <button
                data-testid="delete-bill-btn"
                onClick={onDelete}
                className="border-2 border-red-500 text-red-600 bg-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-red-500 hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </div>
          </div>

          {/* Series manager — only renders if bill is part of a recurring/plan series */}
          <SeriesManager bill={bill} onChanged={() => load()} />

          {/* Payment history */}
          <div className="swiss-card">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-4 w-4" />
              <h2 className="font-display font-extrabold text-lg uppercase tracking-widest">
                Payment History
              </h2>
            </div>
            {(!bill.payments || bill.payments.length === 0) && (
              <div className="text-sm text-neutral-500">
                No payments recorded yet.
              </div>
            )}
            {bill.payments && bill.payments.length > 0 && (
              <ul data-testid="payment-history" className="space-y-4">
                {bill.payments
                  .slice()
                  .reverse()
                  .map((p) => {
                    const hasReceipt = !!p.receipt_image_base64;
                    const isPdf =
                      p.receipt_image_mime &&
                      p.receipt_image_mime.includes("pdf");
                    return (
                      <li
                        key={p.id}
                        className="border-b border-neutral-100 pb-4 last:border-0"
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-display font-bold text-green-700 mono tabular-nums whitespace-nowrap">
                              + {fmtMoney(p.amount)}
                            </div>
                            <div className="text-xs text-neutral-500 mt-0.5">
                              {fmtDateTime(p.paid_at)}
                              {p.method ? ` • ${p.method}` : ""}
                            </div>
                            {p.bank_receipt_number && (
                              <div className="text-xs mt-1">
                                <span className="swiss-label mr-2">
                                  Bank Receipt #
                                </span>
                                <span className="mono">
                                  {p.bank_receipt_number}
                                </span>
                              </div>
                            )}
                            {p.note && (
                              <div className="text-sm mt-1">{p.note}</div>
                            )}
                            {hasReceipt && (
                              <div className="mt-2">
                                {isPdf ? (
                                  <a
                                    href={`data:${p.receipt_image_mime};base64,${p.receipt_image_base64}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`payment-receipt-view-${p.id}`}
                                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-700 hover:text-black border border-neutral-300 hover:border-black px-3 py-2"
                                  >
                                    <Paperclip className="h-3.5 w-3.5" />
                                    View receipt PDF
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                ) : (
                                  <a
                                    href={`data:${p.receipt_image_mime};base64,${p.receipt_image_base64}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`payment-receipt-view-${p.id}`}
                                    className="block"
                                  >
                                    <img
                                      alt="Bank receipt"
                                      src={`data:${p.receipt_image_mime};base64,${p.receipt_image_base64}`}
                                      className="max-h-32 w-auto border border-neutral-200 hover:border-black transition-colors"
                                    />
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            data-testid={`email-payment-receipt-${p.id}`}
                            onClick={() => onEmailPaymentReceipt(p)}
                            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2 flex-shrink-0"
                          >
                            <ReceiptIcon className="h-3.5 w-3.5" />
                            Email Receipt
                          </button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>

          {/* Reminders */}
          <div className="swiss-card">
            <div className="flex items-center gap-2 mb-4">
              <BellRing className="h-4 w-4" />
              <h2 className="font-display font-extrabold text-lg uppercase tracking-widest">
                Reminder History
              </h2>
            </div>
            {(!bill.reminders || bill.reminders.length === 0) && (
              <div className="text-sm text-neutral-500">
                No reminders sent yet.
              </div>
            )}
            {bill.reminders && bill.reminders.length > 0 && (
              <ul data-testid="reminder-history" className="space-y-3">
                {bill.reminders
                  .slice()
                  .reverse()
                  .map((r) => (
                    <li
                      key={r.id}
                      className="flex items-start gap-3 border-b border-neutral-100 pb-3 last:border-0"
                    >
                      {r.channel === "email" ? (
                        <Mail className="h-4 w-4 mt-0.5 text-blue-600" />
                      ) : (
                        <MessageSquare className="h-4 w-4 mt-0.5 text-indigo-600" />
                      )}
                      <div>
                        <div className="text-sm font-bold uppercase tracking-widest">
                          {r.channel === "email" ? "Email" : "SMS"}
                          {r.auto && (
                            <span className="ml-2 chip bg-neutral-50 text-neutral-600 border-neutral-300">
                              Auto
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          {fmtDateTime(r.sent_at)}
                        </div>
                        {r.note && (
                          <div className="text-sm mt-1">{r.note}</div>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <div className="swiss-card" data-testid="bank-details">
            <div className="swiss-label mb-3">Bank / Payment</div>
            <div className="text-sm space-y-3">
              <div>
                <span className="swiss-label">Account Name</span>
                <div className="mt-1">{bill.account_name || "—"}</div>
              </div>
              <div>
                <span className="swiss-label">Bank</span>
                <div className="mt-1">{bill.bank_name || "—"}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="swiss-label">BSB</span>
                  <div className="mt-1 mono">{bill.bsb || "—"}</div>
                </div>
                <div>
                  <span className="swiss-label">Account No.</span>
                  <div className="mt-1 mono">{bill.account_number || "—"}</div>
                </div>
              </div>
              <div>
                <span className="swiss-label">Reference</span>
                <div className="mt-1">
                  {bill.payment_reference || bill.invoice_number || "—"}
                </div>
              </div>
              {(bill.bpay_biller_code || bill.bpay_reference) && (
                <div className="pt-3 border-t border-neutral-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="swiss-label">BPAY</span>
                    <span className="chip bg-blue-50 text-blue-800 border-blue-200 text-[9px]">
                      B
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="swiss-label">Biller Code</span>
                      <div className="mt-1 mono">
                        {bill.bpay_biller_code || "—"}
                      </div>
                    </div>
                    <div>
                      <span className="swiss-label">Reference (CRN)</span>
                      <div className="mt-1 mono break-all">
                        {bill.bpay_reference || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {bill.payid && (
                <div>
                  <span className="swiss-label">PayID</span>
                  <div className="mt-1 break-all">{bill.payid}</div>
                </div>
              )}
              {bill.payment_url && (
                <div>
                  <span className="swiss-label">Online Payment</span>
                  <div className="mt-1">
                    <a
                      href={bill.payment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-black text-neutral-700 break-all"
                    >
                      {bill.payment_url}
                    </a>
                  </div>
                </div>
              )}
              {bill.accepted_methods && (
                <div>
                  <span className="swiss-label">Other Methods</span>
                  <div className="mt-1">{bill.accepted_methods}</div>
                </div>
              )}
            </div>
          </div>

          <div className="swiss-card" data-testid="contact-panel">
            <div className="swiss-label mb-3">Contact</div>
            <div className="text-sm space-y-2">
              <div>
                <span className="swiss-label">Email</span>
                <div className="mt-1">{bill.email || "—"}</div>
              </div>
              <div>
                <span className="swiss-label">Phone</span>
                <div className="mt-1">{bill.phone || "—"}</div>
              </div>
              <CcEditor bill={bill} onSaved={setBill} />
              <JobTagsEditor bill={bill} onSaved={setBill} />
              <div>
                <span className="swiss-label">Notes</span>
                <div className="mt-1 whitespace-pre-wrap">
                  {bill.notes || "—"}
                </div>
              </div>
              <div className="pt-2">
                <span className="swiss-label">Emailed</span>
                <div className="mt-1">
                  {bill.emailed
                    ? fmtDateTime(bill.emailed_at)
                    : "Not yet"}
                </div>
              </div>
              <div>
                <span className="swiss-label">Texted</span>
                <div className="mt-1">
                  {bill.texted ? fmtDateTime(bill.texted_at) : "Not yet"}
                </div>
              </div>
              <div className="pt-2">
                <span className="swiss-label">Created</span>
                <div className="mt-1">{fmtDateTime(bill.created_at)}</div>
              </div>
            </div>
          </div>

          {bill.image_base64 && (
            <div className="swiss-card">
              <div className="swiss-label mb-3">Bill Image</div>
              <img
                data-testid="bill-image"
                src={`data:${bill.image_mime || "image/jpeg"};base64,${bill.image_base64}`}
                alt="Bill"
                className="w-full border border-neutral-200"
              />
            </div>
          )}
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent
          data-testid="payment-dialog"
          className="rounded-none border-2 border-black max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <label className="block">
              <div className="swiss-label mb-1">
                Amount (Remaining: {fmtMoney(bill.remaining_balance)})
              </div>
              <Input
                data-testid="payment-amount"
                className="swiss-input mono"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
              />
            </label>
            <label className="block">
              <div className="swiss-label mb-1">Method (optional)</div>
              <Input
                data-testid="payment-method"
                className="swiss-input"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                placeholder="Bank transfer, card, cash…"
              />
            </label>
            <label className="block">
              <div className="swiss-label mb-1">Bank Receipt Number (optional)</div>
              <Input
                data-testid="payment-receipt-number"
                className="swiss-input mono"
                value={payReceiptNumber}
                onChange={(e) => setPayReceiptNumber(e.target.value)}
                placeholder="e.g. NAB confirmation #123456789"
              />
            </label>
            <div>
              <div className="swiss-label mb-2">
                Bank Receipt (optional — image or PDF)
              </div>
              <input
                ref={payReceiptInputRef}
                data-testid="payment-receipt-file"
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => onPickReceipt(e.target.files?.[0])}
              />
              <button
                type="button"
                data-testid="payment-receipt-upload-btn"
                onClick={() => payReceiptInputRef.current?.click()}
                className="w-full border-2 border-dashed border-neutral-300 hover:border-black bg-white text-neutral-700 font-bold uppercase tracking-widest text-xs px-4 py-4 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Paperclip className="h-4 w-4" />
                {payReceiptName ? payReceiptName : "Attach receipt"}
              </button>
              {payReceiptBase64 && (
                <button
                  type="button"
                  onClick={() => {
                    setPayReceiptBase64(null);
                    setPayReceiptMime(null);
                    setPayReceiptName("");
                  }}
                  className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-red-600 mt-2"
                >
                  Remove attachment
                </button>
              )}
            </div>
            <label className="block">
              <div className="swiss-label mb-1">Note (optional)</div>
              <Textarea
                data-testid="payment-note"
                className="swiss-input resize-none"
                rows={2}
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
              />
            </label>
          </div>
          <DialogFooter className="gap-2">
            <button
              data-testid="payment-cancel-btn"
              onClick={() => setPayOpen(false)}
              className="border-2 border-neutral-300 bg-white text-neutral-700 font-bold uppercase tracking-widest text-xs px-4 py-3 hover:border-black hover:text-black"
            >
              Cancel
            </button>
            <button
              data-testid="payment-save-btn"
              onClick={onAddPayment}
              disabled={busy}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Payment
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillDetail;
